import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

const empty = { name: "", email: "", subject: "", message: "", consent: false, bild_url: "" };

export default function KontaktSida() {
  const [data, setData] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {message, delivered}
  const [serverError, setServerError] = useState("");
  const [uploading, setUploading] = useState(false);

  usePageMeta({
    title: "Kontakta redaktionen – vidalv.live",
    description: "Skriv till redaktionen för vidalv.live – frågor, tips, rättelser eller idéer. Vi svarar inom några dagar.",
    path: "/kontakt",
  });

  const update = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    setServerError("");
  };

  const handleBildUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setServerError("Bilden är för stor (max 10 MB).");
      return;
    }
    setUploading(true);
    setServerError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("bild_url", res.data.url || "");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : "Kunde inte ladda upp bilden – försök igen.";
      setServerError(msg);
    }
    setUploading(false);
    if (e.target) e.target.value = "";
  };

  const validate = () => {
    const e = {};
    if (!data.name.trim() || data.name.trim().length < 2) e.name = "Ange ditt namn (minst 2 tecken).";
    if (!data.email.trim() || !EMAIL_RE.test(data.email.trim())) e.email = "Ange en giltig e-postadress.";
    if (!data.subject.trim() || data.subject.trim().length < 3) e.subject = "Ange ett ämne (minst 3 tecken).";
    if (!data.message.trim() || data.message.trim().length < 10) e.message = "Ange ett meddelande (minst 10 tecken).";
    if (data.message.length > 5000) e.message = "Meddelandet är för långt (max 5000 tecken).";
    if (!data.consent) e.consent = "Samtycke krävs för att skicka meddelandet.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const eMap = validate();
    if (Object.keys(eMap).length > 0) {
      setErrors(eMap);
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      const { data: res } = await axios.post(`${API}/contact`, {
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        subject: data.subject.trim(),
        message: data.message.trim(),
        consent: data.consent,
        bild_url: data.bild_url || null,
      });
      setSuccess({ message: res.message || "Tack! Meddelandet är mottaget.", delivered: !!res.delivered });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : "Något gick fel. Försök igen om en stund.");
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <article data-testid="kontakt-success" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <CheckCircle2 size={56} className="mx-auto text-pine mb-4" />
        <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl mb-3">Tack!</h1>
        <p className="text-base text-bark leading-relaxed mb-6">{success.message}</p>
        {!success.delivered && (
          <p className="text-xs text-bark/60 mb-4 italic">
            Vi har sparat ditt meddelande säkert hos redaktionen även om den automatiska aviseringen blev fördröjd.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            data-testid="kontakt-back-home"
            className="inline-flex items-center gap-1.5 bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-2.5 rounded-sm no-underline transition-colors"
          >
            <ArrowLeft size={14} /> Till startsidan
          </Link>
          <button
            onClick={() => { setData(empty); setSuccess(null); }}
            data-testid="kontakt-send-another"
            className="inline-flex items-center gap-1.5 bg-bone-alt hover:bg-warm-border text-ink font-body font-semibold text-sm px-5 py-2.5 rounded-sm border border-warm-border transition-colors"
          >
            Skicka ett till
          </button>
        </div>
      </article>
    );
  }

  return (
    <article className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-6">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Mail size={28} className="text-falu" />
        <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl">Kontakta redaktionen</h1>
      </div>
      <p className="text-base text-bark leading-relaxed mb-8">
        Skriv till oss om du har idéer, rättelser, frågor om bygden — eller om du vill bli medmoderator.
        Vi läser allt och svarar inom 5 vardagar.
      </p>

      <form onSubmit={handleSubmit} data-testid="kontakt-form" className="flex flex-col gap-5 bg-parchment border border-warm-border rounded-sm p-5 sm:p-7" noValidate>
        <Field label="Namn" htmlFor="kontakt-name" error={errors.name} required>
          <input
            id="kontakt-name"
            data-testid="kontakt-name"
            type="text"
            autoComplete="name"
            value={data.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Ditt namn"
            className={inputClass(errors.name)}
            disabled={submitting}
          />
        </Field>

        <Field label="E-post" htmlFor="kontakt-email" error={errors.email} required>
          <input
            id="kontakt-email"
            data-testid="kontakt-email"
            type="email"
            autoComplete="email"
            value={data.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="namn@exempel.se"
            className={inputClass(errors.email)}
            disabled={submitting}
          />
        </Field>

        <Field label="Ämne" htmlFor="kontakt-subject" error={errors.subject} required>
          <input
            id="kontakt-subject"
            data-testid="kontakt-subject"
            type="text"
            value={data.subject}
            onChange={(e) => update("subject", e.target.value)}
            placeholder="Vad gäller det?"
            maxLength={120}
            className={inputClass(errors.subject)}
            disabled={submitting}
          />
        </Field>

        <Field label="Meddelande" htmlFor="kontakt-message" error={errors.message} required
               help={`${data.message.length} / 5000 tecken`}>
          <textarea
            id="kontakt-message"
            data-testid="kontakt-message"
            value={data.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder="Skriv ditt meddelande här..."
            rows={7}
            maxLength={5000}
            className={`${inputClass(errors.message)} resize-y min-h-[140px]`}
            disabled={submitting}
          />
        </Field>

        <Field label="Bifoga bild (frivilligt)" htmlFor="kontakt-bild">
          {data.bild_url ? (
            <div className="flex items-center gap-3 bg-bone-alt/40 border border-warm-border rounded-sm p-2">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${data.bild_url}`} alt="Bifogad" className="w-20 h-20 object-cover rounded-sm" />
              <button
                type="button"
                data-testid="kontakt-bild-remove"
                onClick={() => update("bild_url", "")}
                className="text-xs text-falu hover:text-falu-hover underline"
              >
                Ta bort bild
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 text-sm text-pine hover:text-pine-hover cursor-pointer">
              {uploading ? "Laddar upp..." : "Välj bild från enheten"}
              <input
                id="kontakt-bild"
                data-testid="kontakt-bild-input"
                type="file"
                accept="image/*"
                onChange={handleBildUpload}
                disabled={uploading || submitting}
                className="hidden"
              />
            </label>
          )}
        </Field>

        <label className={`flex items-start gap-2 cursor-pointer text-sm ${errors.consent ? "text-falu" : "text-bark"}`}>
          <input
            type="checkbox"
            data-testid="kontakt-consent"
            checked={data.consent}
            onChange={(e) => update("consent", e.target.checked)}
            className="mt-1 shrink-0 accent-falu"
            disabled={submitting}
          />
          <span>
            Jag samtycker till att mitt meddelande behandlas i syfte att besvaras.
            Se vår <Link to="/integritetspolicy" className="text-falu underline">integritetspolicy</Link>.
          </span>
        </label>
        {errors.consent && <p className="text-xs text-falu -mt-3" data-testid="kontakt-consent-error">{errors.consent}</p>}

        {serverError && (
          <div data-testid="kontakt-server-error" role="alert"
               className="flex items-start gap-2 bg-falu/10 border border-falu/30 text-falu rounded-sm px-4 py-3 text-sm">
            <AlertCircle size={16} className="shrink-0 mt-0.5" /> {serverError}
          </div>
        )}

        <button
          type="submit"
          data-testid="kontakt-submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-falu hover:bg-falu-hover disabled:bg-bark/40 disabled:cursor-not-allowed text-parchment font-body font-semibold text-sm px-6 py-3 rounded-sm transition-colors"
        >
          {submitting ? (
            <>Skickar...</>
          ) : (
            <><Send size={15} /> Skicka meddelande</>
          )}
        </button>
      </form>
    </article>
  );
}

function Field({ label, htmlFor, error, required, help, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-ink">
        {label} {required && <span className="text-falu">*</span>}
      </label>
      {children}
      {error
        ? <p className="text-xs text-falu" data-testid={`${htmlFor}-error`}>{error}</p>
        : help ? <p className="text-xs text-bark/60">{help}</p> : null}
    </div>
  );
}

function inputClass(hasError) {
  return [
    "w-full px-4 py-2.5 rounded-sm bg-bone border text-ink",
    "focus:ring-2 focus:ring-pine focus:outline-none",
    hasError ? "border-falu" : "border-warm-border",
  ].join(" ");
}
